import React from 'react'
import { useGetEnterpriseAccountQuery } from 'domains/account-management/api'
import { AccountManagementAccountForm } from 'domains/account-management/components/account-form/AccountManagementAccountForm'
import { useUpdateEnterpriseAccount } from 'domains/account-management/api/enterprise-account/update/useUpdateEnterpriseAccount.hooks'
import { AccountUpdateLoading } from 'domains/account-management/components/account-update/components/loading/AccountUpdateLoading'
import { useUpdateAccount } from 'domains/account-management/components/account-update/utils/useUpdateAccount.hooks'
import { AccountUpdateGetAccountError } from 'domains/account-management/components/account-update/components/error/AccountUpdateGetAccountError'
import { useCurrentAccountDetails } from 'api/current-account/currentAccount.hooks'
import { type RetailerTerminology } from 'domains/account-management/components/account-form/utils/AccountForm.types'
import { useIsReadOnly } from 'gin-and-tonic/utils/access/useIsReadOnly'
import {
  SuccessNotification,
  ErrorNotification,
} from './components/notifications/AccountUpdateNotifications'

interface Props {
  showAllPartnersFullAccessToggle?: boolean
  retailerTerminology: RetailerTerminology
  canonicalId: string
  partnerId?: string
}

export const AccountManagementUpdateAccount: React.FunctionComponent<Props> = ({
  canonicalId,
  showAllPartnersFullAccessToggle,
  retailerTerminology,
  partnerId,
}) => {
  const { submit, apiLoading } = useUpdateEnterpriseAccount()
  const {
    loading: currentAccountLoading,
    error: currentAccountError,
    account: currentAccount,
  } = useCurrentAccountDetails()

  const {
    refetch,
    data,
    loading: accountLoading,
    error,
  } = useGetEnterpriseAccountQuery({
    variables: { canonicalId },
    fetchPolicy: 'network-only',
  })

  const { initialFormData, onFormSubmit } = useUpdateAccount({
    submit,
    accountData: data,
  })

  const isReadOnly = useIsReadOnly()

  if (error || currentAccountError)
    return <AccountUpdateGetAccountError onRetry={() => refetch()} />
  if (accountLoading || currentAccountLoading) return <AccountUpdateLoading />

  const {
    hasAllPartnersFullAccess: allowAllPartnerFullAccess,
    hasFullPartnerAccess: allowFullPartnerAccess,
    hasFullRetailerAccess: allowFullRetailerAccess,
  } = currentAccount.internalAccessConfiguration

  return (
    <AccountManagementAccountForm
      initialState={initialFormData}
      showAllPartnersFullAccessToggle={showAllPartnersFullAccessToggle}
      retailerTerminology={retailerTerminology}
      allowAllPartnersFullAccess={allowAllPartnerFullAccess}
      allowFullPartnerAccess={allowFullPartnerAccess}
      allowFullRetailerAccess={allowFullRetailerAccess}
      onFormSubmit={onFormSubmit}
      formLoading={apiLoading}
      disableFields={{ email: true }}
      SuccessNotification={SuccessNotification}
      ErrorNotification={ErrorNotification}
      partnerId={partnerId}
      isCurrentAccountReadOnly={isReadOnly}
      isUpdate
    />
  )
}
